/**
 * Animaties van De Wijntoren (Thema-instellingen > De Wijntoren > Animaties, via data-motion op dit script).
 * - Inkomen: de inhoud van dw-secties komt binnen zodra hij in beeld komt; kaarten, tegels en vragen één voor één.
 *   Wat bij het laden al in beeld staat, blijft gewoon staan (de bovenkant van de pagina wacht nooit).
 *   Een sectie met data-dw-anim="uit" (instelling Animatie in de sectie) doet niet mee.
 * - Iconen (snippets/dw-icon.liquid) tekenen zichzelf zodra ze in beeld komen.
 * - Stempels (snippets/dw-stamp.liquid) worden erop gestempeld zodra ze in beeld komen.
 * - Achtergronden met data-dw-bg="diepte" bewegen trager mee (diepte); "zoom" is pure CSS.
 * - Elementen met data-dw-play krijgen .is-on zodra ze in beeld komen (bijv. de Toren-smaakschaal die vult).
 * Het karakter (afstand, kanteling, vering, gloed) staat als CSS-variabelen in de stijlrichtingbestanden.
 * Uit bij 'minder beweging' en in de theme editor.
 */
(() => {
  const MODE = document.currentScript?.dataset.motion || 'rustig';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const design = Boolean(window.Shopify && window.Shopify.designMode);
  const enabled = MODE !== 'uit' && !reduce && !design && 'IntersectionObserver' in window;
  document.documentElement.dataset.dwMotion = enabled ? MODE : 'uit';
  if (!enabled) return;

  // Losse elementen in rasters en lijsten; die komen één voor één binnen.
  const ITEMS = [
    '.cat', '.prod', '.card', '.pth', '.art', '.usp', '.deliv > div', '.bn', '.q', '.house',
    '.facts li', '.chips li', '.chips2 li', '.menu > div', '.dw-ev__card', '.dw-faq__item', '.dw-cd__unit',
    '.dw-vibe__aroma', '.dw-vibe__pair', '.dw-vibe__fact', '.dw-route__step',
  ].join(',');
  const SKIP_SECTIONS = '.chero, .dw-ss, .dw-scroll, [data-dw-anim="uit"]';
  const STAGGER_MAX = 8;

  const inView = (el) => {
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  };

  const reveal = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        reveal.unobserve(el);
        el.classList.add('is-in');
        // Daarna de animatieklassen weghalen, zodat hover-overgangen van het element weer werken.
        const done = () => el.classList.remove('dw-rv', 'is-in');
        el.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 2000);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
  );

  const draw = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        draw.unobserve(entry.target);
        const el = entry.target;
        el.classList.add(el.classList.contains('dw-stamp') ? 'is-stamped' : el.hasAttribute('data-dw-play') ? 'is-on' : 'is-drawn');
      });
    },
    { threshold: 0.6 }
  );

  const prepare = (el, index) => {
    if (inView(el)) return;
    el.classList.add('dw-rv');
    el.style.setProperty('--dw-i', Math.min(index, STAGGER_MAX));
    reveal.observe(el);
  };

  const scan = (root = document) => {
    root.querySelectorAll('.hp').forEach((hp) => {
      if (hp.matches(SKIP_SECTIONS)) return;
      hp.querySelectorAll(':scope > .in, :scope .in').forEach((inner) => {
        if (inner.dataset.dwScanned) return;
        inner.dataset.dwScanned = 'true';
        let index = 0;
        [...inner.children].forEach((child) => {
          const items = [...child.querySelectorAll(ITEMS)].filter((item) => !item.parentElement.closest(ITEMS));
          if (child.matches(ITEMS)) prepare(child, index++);
          else if (items.length > 1) items.forEach((item, i) => prepare(item, index + i));
          else prepare(child, index++);
        });
      });
    });

    // Elementen met data-dw-play (bijv. de Toren-smaakschaal): .dw-wait tot ze in beeld zijn, dan .is-on.
    root.querySelectorAll('[data-dw-play]:not(.dw-wait)').forEach((el) => {
      if (inView(el) || el.closest('[data-dw-anim="uit"]')) return;
      el.classList.add('dw-wait');
      draw.observe(el);
    });

    root.querySelectorAll('.dw-stamp:not(.dw-stamp-wait)').forEach((stamp) => {
      if (inView(stamp)) return;
      stamp.classList.add('dw-stamp-wait');
      draw.observe(stamp);
    });

    root.querySelectorAll('.dw-icon:not(.dw-draw)').forEach((icon) => {
      if (inView(icon)) return;
      icon.querySelectorAll('[pathLength]').forEach((path, i) => path.style.setProperty('--dw-p', i));
      icon.classList.add('dw-draw');
      draw.observe(icon);
    });
  };

  // Diepte bij scrollen: achtergronden met data-dw-bg="diepte" schuiven langzamer mee dan de pagina.
  let ticking = false;
  const parallax = () => {
    ticking = false;
    document.querySelectorAll('[data-dw-bg="diepte"]').forEach((bg) => {
      const r = bg.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const shift = (r.top + r.height / 2 - window.innerHeight / 2) * -0.12;
      bg.style.setProperty('--dw-bg-shift', `${shift.toFixed(1)}px`);
    });
  };
  // capture: Horizon scrolt op desktop binnen .page-wrapper, niet in het venster
  document.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(parallax);
      }
    },
    { passive: true, capture: true }
  );

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { scan(); parallax(); });
  else { scan(); parallax(); }
})();
